import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, Calculator, X, TrendingUp, DollarSign, AlertCircle } from 'lucide-react';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingRules = () => {
  const { admin, getAuthHeader } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin';
  
  const [rules, setRules] = useState([]);
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [computing, setComputing] = useState(null);
  const [showSchengenSelector, setShowSchengenSelector] = useState(false);
  const [showOtherCountryInput, setShowOtherCountryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    service_type: '',
    destination_country: '',
    schengen_country: '',
    other_country: '',
    sources: [],
    vendor_cost_per_applicant: '',
    embassy_fees_per_applicant: '',
    vfs_bls_charges_per_applicant: '',
    total_monthly_overhead: '',
    expected_monthly_volume: '',
    operational_overheads: 0,
    bulk_discounts: [],
    min_margin_percent: 15,
    target_margin_percent: 25,
    is_active: true
  });
  
  // State for adding new discount tier
  const [newDiscountTier, setNewDiscountTier] = useState({ num_applicants: '', discount_percent: '' });

  useEffect(() => {
    fetchServices();
    fetchCountries();
    fetchSchengenCountries();
    fetchSources();
    fetchRules();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, { headers: getAuthHeader() });
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schengen-countries`);
      setSchengenCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch Schengen countries:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API}/sources/`, { headers: getAuthHeader() });
      setSources(response.data);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    }
  };

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/pricing-rules`, {
        headers: getAuthHeader()
      });
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      alert('Failed to fetch pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isSuperAdmin) {
      alert('Only super admins can create/edit pricing rules');
      return;
    }

    // Determine the final country value
    let finalCountry = formData.destination_country;
    if (formData.destination_country === 'Schengen (Europe)') {
      finalCountry = formData.schengen_country;
    } else if (formData.destination_country === 'Other') {
      finalCountry = formData.other_country;
    }

    if (!finalCountry) {
      alert('Please select or specify a destination country');
      return;
    }

    const submitData = {
      ...formData,
      country: finalCountry,
      vendor_cost_per_applicant: parseFloat(formData.vendor_cost_per_applicant) || 0,
      embassy_fees_per_applicant: parseFloat(formData.embassy_fees_per_applicant) || 0,
      vfs_bls_charges_per_applicant: parseFloat(formData.vfs_bls_charges_per_applicant) || 0,
      total_monthly_overhead: parseFloat(formData.total_monthly_overhead) || 0,
      expected_monthly_volume: parseFloat(formData.expected_monthly_volume) || 1,
      min_margin_percent: parseFloat(formData.min_margin_percent) || 15,
      target_margin_percent: parseFloat(formData.target_margin_percent) || 25
    };
    
    try {
      if (editingRule) {
        await axios.put(
          `${API}/admin/pricing-rules/${editingRule.id}`,
          submitData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/admin/pricing-rules`,
          submitData,
          { headers: getAuthHeader() }
        );
      }
      
      setShowModal(false);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Failed to save rule:', error);
      alert(error.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleEdit = (rule) => {
    if (!isSuperAdmin) {
      alert('Only super admins can edit pricing rules');
      return;
    }
    
    setEditingRule(rule);
    
    // Check if the country is in the main countries list
    const isMainCountry = countries.some(c => c.name === rule.country);
    const isSchengenCountry = schengenCountries.some(c => c.name === rule.country);
    
    let destination_country = rule.country;
    let schengen_country = '';
    let other_country = '';
    
    if (isSchengenCountry && !isMainCountry) {
      destination_country = 'Schengen (Europe)';
      schengen_country = rule.country;
      setShowSchengenSelector(true);
      setShowOtherCountryInput(false);
    } else if (!isMainCountry && !isSchengenCountry) {
      destination_country = 'Other';
      other_country = rule.country;
      setShowSchengenSelector(false);
      setShowOtherCountryInput(true);
    } else {
      setShowSchengenSelector(false);
      setShowOtherCountryInput(false);
    }
    
    setFormData({
      service_type: rule.service_type,
      destination_country,
      schengen_country,
      other_country,
      sources: rule.sources || [],
      vendor_cost_per_applicant: rule.vendor_cost_per_applicant,
      embassy_fees_per_applicant: rule.embassy_fees_per_applicant,
      vfs_bls_charges_per_applicant: rule.vfs_bls_charges_per_applicant,
      total_monthly_overhead: rule.total_monthly_overhead || 0,
      expected_monthly_volume: rule.expected_monthly_volume || 1,
      operational_overheads: rule.operational_overheads,
      bulk_discounts: rule.bulk_discounts || [],
      min_margin_percent: rule.min_margin_percent || 15,
      target_margin_percent: rule.target_margin_percent || 25,
      is_active: rule.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!isSuperAdmin) {
      alert('Only super admins can delete pricing rules');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this pricing rule?')) return;
    
    try {
      await axios.delete(`${API}/admin/pricing-rules/${id}`, {
        headers: getAuthHeader()
      });
      fetchRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert('Failed to delete rule');
    }
  };

  const handleComputePrice = async (rule) => {
    if (!isSuperAdmin) {
      alert('Only super admins can compute prices');
      return;
    }
    
    try {
      setComputing(rule.id);
      const response = await axios.post(
        `${API}/admin/pricing-rules/${rule.id}/compute`,
        {},
        { headers: getAuthHeader() }
      );
      
      alert(`Price computed successfully!\n\nSuggested Price: ₹${response.data.suggested_price_per_applicant.toLocaleString('en-IN')}\nMargin: ${response.data.margin_percent}%\nBased on ${response.data.competitor_count} competitor(s)`);
      fetchRules();
    } catch (error) {
      console.error('Failed to compute price:', error);
      alert(error.response?.data?.detail || 'Failed to compute price');
    } finally {
      setComputing(null);
    }
  };

  const resetForm = () => {
    setFormData({
      service_type: '',
      destination_country: '',
      schengen_country: '',
      other_country: '',
      sources: [],
      vendor_cost_per_applicant: '',
      embassy_fees_per_applicant: '',
      vfs_bls_charges_per_applicant: '',
      total_monthly_overhead: '',
      expected_monthly_volume: '',
      operational_overheads: 0,
      min_margin_percent: 15,
      target_margin_percent: 25,
      is_active: true
    });
    setEditingRule(null);
    setShowSchengenSelector(false);
    setShowOtherCountryInput(false);
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const calculateTotalCost = (rule) => {
    return rule.vendor_cost_per_applicant + rule.embassy_fees_per_applicant + rule.vfs_bls_charges_per_applicant;
  };

  const calculateOperationalOverheads = (totalMonthly, expectedVolume) => {
    const overhead = parseFloat(totalMonthly) || 0;
    const volume = parseFloat(expectedVolume) || 1;
    if (volume <= 0) return 0;
    return Math.round(overhead / volume);
  };

  // Effect to auto-calculate operational overheads when inputs change
  useEffect(() => {
    const calculated = calculateOperationalOverheads(
      formData.total_monthly_overhead,
      formData.expected_monthly_volume
    );
    if (calculated !== formData.operational_overheads) {
      setFormData(prev => ({ ...prev, operational_overheads: calculated }));
    }
  }, [formData.total_monthly_overhead, formData.expected_monthly_volume]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Visaasure Pricing Rules</h1>
          <p className="page-description">
            {isSuperAdmin 
              ? 'Define internal costs and compute best pricing' 
              : 'View pricing rules (Admin access - margins hidden)'}
          </p>
        </div>
        {isSuperAdmin && (
          <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={20} />
            Create Pricing Rule
          </button>
        )}
      </div>

      {/* Info Card */}
      <div className="card" style={{ marginBottom: '24px', backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
          <AlertCircle size={20} style={{ color: '#0ea5e9', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h3 style={{ margin: 0, marginBottom: '8px', color: '#0c4a6e' }}>How it works</h3>
            <ol style={{ margin: 0, paddingLeft: '20px', color: '#0c4a6e' }}>
              <li>Add competitor pricing data in the Competitor Pricing page</li>
              <li>Create pricing rules with your internal costs for each Service + Country + Source(s)</li>
              <li><strong>Multiple rules allowed:</strong> You can create different rules for the same Service + Country if they target different lead sources (e.g., higher margins for direct leads vs marketplace leads)</li>
              <li>Click "Compute Best Price" to calculate optimal pricing based on market data</li>
              <li>Sales consultants see the right suggested price based on the lead's source</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        ) : rules.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <DollarSign size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
            <p>No pricing rules found. Create your first rule to get started.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Country</th>
                  <th>Sources</th>
                  <th>Cost/Applicant</th>
                  <th>Fixed Costs</th>
                  {isSuperAdmin && <th>Min Margin</th>}
                  {isSuperAdmin && <th>Target Margin</th>}
                  <th>Suggested Price</th>
                  <th>Last Computed</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(rule => {
                  const costPerApplicant = calculateTotalCost(rule);
                  const hasPrice = rule.suggested_price_per_applicant > 0;
                  
                  return (
                    <tr key={rule.id}>
                      <td><strong>{rule.service_type}</strong></td>
                      <td>{rule.country}</td>
                      <td>
                        {rule.sources && rule.sources.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {rule.sources.map(source => (
                              <span 
                                key={source}
                                style={{
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  padding: '2px 8px',
                                  borderRadius: '8px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}
                              >
                                {source}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#666', fontSize: '12px' }}>All Sources</span>
                        )}
                      </td>
                      <td>{formatCurrency(costPerApplicant)}</td>
                      <td>{formatCurrency(rule.operational_overheads)}</td>
                      {isSuperAdmin && <td>{rule.min_margin_percent}%</td>}
                      {isSuperAdmin && <td>{rule.target_margin_percent}%</td>}
                      <td>
                        {hasPrice ? (
                          <strong style={{ color: '#059669' }}>{formatCurrency(rule.suggested_price_per_applicant)}</strong>
                        ) : (
                          <span style={{ color: '#dc2626' }}>Not computed</span>
                        )}
                      </td>
                      <td>
                        {rule.last_computed_at 
                          ? new Date(rule.last_computed_at).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        <span className={`badge ${rule.is_active ? 'badge-success' : 'badge-secondary'}`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {isSuperAdmin && (
                            <>
                              <button 
                                className="btn-icon" 
                                onClick={() => handleComputePrice(rule)}
                                disabled={computing === rule.id}
                                title="Compute Best Price"
                                style={{
                                  background: '#22c55e',
                                  color: 'white',
                                  border: '1px solid #16a34a',
                                  opacity: computing === rule.id ? 0.6 : 1
                                }}
                              >
                                {computing === rule.id ? '...' : <Calculator size={16} />}
                              </button>
                              <button className="btn-icon" onClick={() => handleEdit(rule)} title="Edit">
                                <Edit2 size={16} />
                              </button>
                              <button className="btn-icon btn-danger" onClick={() => handleDelete(rule.id)} title="Delete">
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{editingRule ? 'Edit' : 'Create'} Pricing Rule</h2>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Service Type *</label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                    required
                    disabled={editingRule}
                  >
                    <option value="">Select Service</option>
                    {services.map(svc => (
                      <option key={svc.id} value={svc.title}>{svc.title}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Destination Country *</label>
                  <select
                    value={formData.destination_country}
                    onChange={(e) => {
                      const selectedCountry = e.target.value;
                      setFormData({ 
                        ...formData, 
                        destination_country: selectedCountry,
                        schengen_country: selectedCountry === 'Schengen (Europe)' ? formData.schengen_country : '',
                        other_country: selectedCountry === 'Other' ? formData.other_country : ''
                      });
                      setShowSchengenSelector(selectedCountry === 'Schengen (Europe)');
                      setShowOtherCountryInput(selectedCountry === 'Other');
                    }}
                    required
                    disabled={editingRule}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.name} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Schengen Country Selector */}
                {showSchengenSelector && (
                  <div className="form-group" style={{ background: '#f0f9ff', padding: '12px', borderRadius: '6px', border: '1px solid #bae6fd', gridColumn: '1 / -1' }}>
                    <label>Select Specific Schengen Country *</label>
                    <select
                      value={formData.schengen_country || ''}
                      onChange={(e) => setFormData({ ...formData, schengen_country: e.target.value })}
                      required
                      disabled={editingRule}
                      style={{ background: 'white' }}
                    >
                      <option value="">Select Schengen Country</option>
                      {schengenCountries.map(country => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '6px', color: '#0369a1', fontSize: '12px' }}>
                      💡 Select the primary destination country within Schengen area
                    </small>
                  </div>
                )}

                {/* Other Country Input */}
                {showOtherCountryInput && (
                  <div className="form-group" style={{ background: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #fcd34d', gridColumn: '1 / -1' }}>
                    <label>Specify Country Name *</label>
                    <input
                      type="text"
                      value={formData.other_country || ''}
                      onChange={(e) => setFormData({ ...formData, other_country: e.target.value })}
                      placeholder="Enter country name..."
                      required
                      disabled={editingRule}
                      style={{ background: 'white' }}
                    />
                    <small style={{ display: 'block', marginTop: '6px', color: '#92400e', fontSize: '12px' }}>
                      💡 Please enter the destination country not listed above
                    </small>
                  </div>
                )}

                {/* Lead Sources Multi-Select */}
                <div className="form-group" style={{ gridColumn: '1 / -1', background: '#fef9e7', padding: '12px', borderRadius: '6px', border: '1px solid #f9d84f' }}>
                  <label>Applicable Lead Sources (Optional)</label>
                  <select
                    multiple
                    value={formData.sources}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, sources: selected });
                    }}
                    style={{ 
                      minHeight: '120px', 
                      background: 'white',
                      width: '100%',
                      padding: '8px'
                    }}
                  >
                    {sources.map(source => (
                      <option key={source.id} value={source.name}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ display: 'block', marginTop: '6px', color: '#92400e', fontSize: '12px' }}>
                    💡 <strong>Select sources this rule applies to.</strong> Leave empty to apply to ALL sources. Direct leads typically have better margins than marketplace leads.
                    <br/>
                    Hold Ctrl (Windows) or Cmd (Mac) to select multiple sources.
                  </small>
                  {formData.sources.length > 0 && (
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {formData.sources.map(source => (
                        <span 
                          key={source}
                          style={{
                            background: '#fbbf24',
                            color: '#78350f',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {source}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                sources: formData.sources.filter(s => s !== source)
                              });
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#78350f',
                              cursor: 'pointer',
                              padding: '0',
                              marginLeft: '2px',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <h3 style={{ margin: '16px 0 8px 0', fontSize: '16px' }}>Variable Costs (Per Applicant)</h3>
                </div>

                <div className="form-group">
                  <label>Vendor Cost (₹)</label>
                  <input
                    type="number"
                    value={formData.vendor_cost_per_applicant}
                    onChange={(e) => setFormData({...formData, vendor_cost_per_applicant: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Embassy Fees (₹)</label>
                  <input
                    type="number"
                    value={formData.embassy_fees_per_applicant}
                    onChange={(e) => setFormData({...formData, embassy_fees_per_applicant: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>VFS/BLS Charges (₹)</label>
                  <input
                    type="number"
                    value={formData.vfs_bls_charges_per_applicant}
                    onChange={(e) => setFormData({...formData, vfs_bls_charges_per_applicant: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <h3 style={{ margin: '16px 0 8px 0', fontSize: '16px' }}>Operational Overhead Calculation</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                    Formula: Total Monthly Overhead ÷ Expected Monthly Volume
                  </p>
                </div>

                <div className="form-group">
                  <label>Total Monthly Overhead (₹) *</label>
                  <input
                    type="number"
                    value={formData.total_monthly_overhead}
                    onChange={(e) => setFormData({...formData, total_monthly_overhead: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                    required
                  />
                  <small style={{ color: '#666' }}>Total fixed monthly costs (rent, salaries, etc.)</small>
                </div>

                <div className="form-group">
                  <label>Expected Monthly Volume *</label>
                  <input
                    type="number"
                    value={formData.expected_monthly_volume}
                    onChange={(e) => setFormData({...formData, expected_monthly_volume: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="1"
                    min="1"
                    required
                  />
                  <small style={{ color: '#666' }}>Expected number of applications per month</small>
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1', background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '2px solid #86efac' }}>
                  <label style={{ color: '#166534', fontWeight: '600' }}>Calculated Operational Overhead (per application)</label>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#166534', marginTop: '8px' }}>
                    ₹{formData.operational_overheads.toLocaleString('en-IN')}
                  </div>
                  <small style={{ color: '#166534', display: 'block', marginTop: '8px' }}>
                    💡 This value is automatically calculated: ₹{(parseFloat(formData.total_monthly_overhead) || 0).toLocaleString('en-IN')} ÷ {parseFloat(formData.expected_monthly_volume) || 1} = ₹{formData.operational_overheads.toLocaleString('en-IN')}
                  </small>
                </div>

                {/* Bulk Discount Section */}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <h3 style={{ margin: '16px 0 8px 0', fontSize: '16px' }}>📦 Bulk Discount Tiers (Optional)</h3>
                  <small style={{ color: '#666', display: 'block', marginBottom: '12px' }}>
                    Offer discounts for families/groups with multiple applicants. E.g., 5% off for 2 applicants, 10% off for 3+, etc.
                  </small>
                </div>

                {/* Add Discount Tier */}
                <div className="form-group" style={{ gridColumn: '1 / -1', background: '#fefce8', padding: '12px', borderRadius: '8px', border: '1px solid #fde047' }}>
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Add Discount Tier</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', color: '#666' }}>Number of Applicants</label>
                      <input
                        type="number"
                        value={newDiscountTier.num_applicants}
                        onChange={(e) => setNewDiscountTier({...newDiscountTier, num_applicants: e.target.value})}
                        placeholder="e.g., 2"
                        min="2"
                        style={{ background: 'white' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', color: '#666' }}>Discount %</label>
                      <input
                        type="number"
                        value={newDiscountTier.discount_percent}
                        onChange={(e) => setNewDiscountTier({...newDiscountTier, discount_percent: e.target.value})}
                        placeholder="e.g., 5"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{ background: 'white' }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (newDiscountTier.num_applicants && newDiscountTier.discount_percent) {
                          const tier = {
                            num_applicants: parseInt(newDiscountTier.num_applicants),
                            discount_percent: parseFloat(newDiscountTier.discount_percent)
                          };
                          setFormData({
                            ...formData,
                            bulk_discounts: [...formData.bulk_discounts, tier].sort((a, b) => a.num_applicants - b.num_applicants)
                          });
                          setNewDiscountTier({ num_applicants: '', discount_percent: '' });
                        }
                      }}
                      className="btn-primary"
                      style={{ minWidth: '100px' }}
                    >
                      + Add
                    </button>
                  </div>
                </div>

                {/* Display Added Discount Tiers */}
                {formData.bulk_discounts && formData.bulk_discounts.length > 0 && (
                  <div className="form-group" style={{ gridColumn: '1 / -1', background: '#f0f9ff', padding: '12px', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Active Discount Tiers</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {formData.bulk_discounts.map((tier, index) => (
                        <div 
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: 'white',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '1px solid #bae6fd'
                          }}
                        >
                          <span style={{ fontWeight: '600', color: '#0c4a6e' }}>
                            {tier.num_applicants}+ applicants → {tier.discount_percent}% discount
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                bulk_discounts: formData.bulk_discounts.filter((_, i) => i !== index)
                              });
                            }}
                            style={{
                              background: '#fee2e2',
                              color: '#991b1b',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <h3 style={{ margin: '16px 0 8px 0', fontSize: '16px' }}>Margin Settings (Super Admin Only)</h3>
                </div>

                <div className="form-group">
                  <label>Minimum Margin (%)</label>
                  <input
                    type="number"
                    value={formData.min_margin_percent}
                    onChange={(e) => setFormData({...formData, min_margin_percent: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="15"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Target Margin (%)</label>
                  <input
                    type="number"
                    value={formData.target_margin_percent}
                    onChange={(e) => setFormData({...formData, target_margin_percent: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="25"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({...formData, is_active: e.target.value === 'active'})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingRule ? 'Update' : 'Create'} Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingRules;
